import React, { useRef } from "react";
import {
  Box,
  Container,
  Typography,
  useTheme,
  Skeleton,
  Stack,
  Chip,
  Paper,
  alpha,
  IconButton,
} from "@mui/material";
import { motion } from "framer-motion";
import {
  Storefront,
  Star,
  People,
  History,
  EmojiEmotions,
  ZoomIn,
} from "@mui/icons-material";
import StatsSct from "./StatsSct";
import BranchesSct from "./BranchesSct";
import HeroSct from "./HeroSct";
import {
  useGetAboutQuery,
  useGetBranchesQuery,
} from "../../../Store/Slices/rtk";

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: "easeOut" } },
};

export default function AboutUs() {
  const theme = useTheme();
  const branchesRef = useRef(null);
  const { data, isLoading } = useGetAboutQuery();
  const { data: branchesData, isLoading: branchLoading } =
    useGetBranchesQuery();

  const aboutData = data?.data;
  // Get branches from real API data
  const branches = branchesData?.data?.branchs || aboutData?.branchs;
  const instagramLink = aboutData?.links?.instagram;

  const galleryImages = aboutData?.images;

  const stats = [
    { icon: <Storefront />, value: branches?.length || 0, label: "شعبه فعال" },
    { icon: <People />, value: "۱۰۰۰+", label: "مشتری راضی" },
    { icon: <History />, value: "۵ سال", label: "سابقه کاری" },
    { icon: <EmojiEmotions />, value: "۴.۹", label: "امتیاز مشتریان" },
  ];

  // Skeletons list
  const skeletonList = [...Array(4)]?.map((_, i) => (
    <Skeleton
      key={i}
      variant="text"
      height={24}
      width={i === 3 ? "60%" : "100%"}
    />
  ));

  // Gallery list
  const galleryList = galleryImages?.map((item, index) => (
    <motion.div
      key={index}
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: index * 0.1 }}
    >
      <Box
        sx={{
          position: "relative",
          borderRadius: 1,
          overflow: "hidden",
          aspectRatio: "3/3",
          boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
          cursor: "pointer",
          "&:hover .overlay": { opacity: 1 },
          "&:hover img": { transform: "scale(1.05)" },
        }}
      >
        <Box
          component="img"
          src={import.meta.env.VITE_URL + item?.file_name}
          alt={item?.alt || item?.title || "تصویر"}
          title={item?.title || item?.alt || "تصویر"}
          loading="lazy"
          sx={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
            transition: "transform 0.5s ease-in-out",
          }}
        />
      </Box>
    </motion.div>
  ));

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "transparent", py: "5rem" }}>
      {/* Hero */}
      {/* <HeroSct
        branchesRef={branchesRef}
        aboutData={aboutData}
        instagramLink={instagramLink}
      /> */}

      {/* Stats */}
      <StatsSct stats={stats} />

      {/* Description */}
      <Container maxWidth="xl" sx={{ py: 6 }}>
        <motion.div
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          <Paper
            elevation={0}
            sx={{
              p: { xs: 3, md: 5 },
              borderRadius: 1,
              bgcolor: "transparent",
              border: `1px solid ${alpha(theme?.palette?.divider, 0.08)}`,
            }}
          >
            {isLoading ? (
              <Stack gap={1.5}>{skeletonList}</Stack>
            ) : (
              <Stack direction="row" gap={2} alignItems="flex-start">
                <Box
                  sx={{
                    minWidth: 36,
                    mt: 0.5,
                    display: { xs: "none", md: "block" },
                  }}
                >
                  <Box
                    sx={{
                      width: 36,
                      height: 36,
                      borderRadius: 1,
                      bgcolor: alpha(theme?.palette?.primary?.main, 0.08),
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Star
                      sx={{
                        color: theme?.palette?.warning?.main,
                        fontSize: 22,
                      }}
                    />
                  </Box>
                </Box>
                <Typography
                  sx={{
                    fontSize: "0.95rem",
                    lineHeight: 2,
                    color: "text.primary",
                    textAlign: "justify",
                    whiteSpace: "pre-wrap",
                    fontWeight: 400,
                  }}
                >
                  {aboutData?.description || "توضیحات مجموعه..."}
                </Typography>
              </Stack>
            )}
          </Paper>
        </motion.div>
      </Container>

      {/* Gallery */}
      <Box sx={{ py: 6, bgcolor: alpha(theme?.palette?.primary?.main, 0.02) }}>
        <Container maxWidth="xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <Stack direction="row" gap={2} alignItems="center" sx={{ mb: 4 }}>
              <Box
                sx={{
                  width: 4,
                  height: 30,
                  bgcolor: "primary.main",
                  borderRadius: 1,
                }}
              />
              <Typography variant="h4" fontWeight={700} fontSize="1.6rem">
                گالری تصاویر
              </Typography>
              <Chip
                label={`${galleryImages?.length || 0} تصویر`}
                color="primary"
                size="small"
                sx={{ fontWeight: 600, fontSize: "0.7rem" }}
              />
            </Stack>
          </motion.div>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "repeat(2, 1fr)",
                lg: "repeat(3, 1fr)",
              },
              gap: 3,
            }}
          >
            {galleryList}
          </Box>
        </Container>
      </Box>

      {/* Branches */}
      <Box ref={branchesRef}>
        <BranchesSct branches={branches} isLoading={branchLoading} />
      </Box>
    </Box>
  );
}
